import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  OutlinedInput,
  FormHelperText,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  ListSubheader,
  Checkbox,
  ListItemText,
  FormControlLabel,
  Switch,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import { connect } from "react-redux";
import { PageHeader, Card, ActionButton } from "../shared";
import ApiServices from "../../config/ApiServices";
import ApiEndpoint from "../../config/ApiEndpoint";
import Constants from "../../config/Constants";
import { MobileOnlyAdBanner } from "./masterOrgAdUtils";

const MasterOrgEditAdvertisement = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  const { id } = useParams();
  const token = props?.profile?.token;

  const [imageId, setImageId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [subOrgs, setSubOrgs] = useState([]);
  const [loadingSubOrgs, setLoadingSubOrgs] = useState(false);
  const [shareToAll, setShareToAll] = useState(false);
  const [selectedClubIds, setSelectedClubIds] = useState([]);
  const clubIdsRef = useRef([]);

  const headers = () => ({ "Content-Type": "application/json", "x-access-token": token });

  useEffect(() => {
    fetchDetails();
    fetchSubOrgs();
  }, []);

  const fetchSubOrgs = async () => {
    setLoadingSubOrgs(true);
    try {
      const data = await ApiServices.GetApiCall(ApiEndpoint.MASTER_ORG_SUB_ORGS, headers());
      if (data?.status && Array.isArray(data.data)) {
        setSubOrgs(data.data);
      }
    } catch {
      toast.error("Could not load clubs.");
    } finally {
      setLoadingSubOrgs(false);
    }
  };

  const fetchDetails = async () => {
    setLoadingDetails(true);
    try {
      const res = await ApiServices.PostApiCall(
        ApiEndpoint.MASTER_ORG_ADVERTISEMENT_VIEW,
        JSON.stringify({ id }),
        headers()
      );
      if (res?.status && res.data) {
        const d = res.data;
        setImageId(d.id_item_logo);
        setImageUrl(d.logo_url);
        const loadedIds = (d.target_org_ids || []).map(Number);
        const isAll = loadedIds.length === 0;
        setShareToAll(isAll);
        clubIdsRef.current = loadedIds;
        setSelectedClubIds(loadedIds);
        formik.setValues({
          websiteLink: d.url_link || "",
        });
      } else {
        toast.error("Failed to load advertisement details.");
      }
    } catch {
      toast.error("Failed to load advertisement details.");
    } finally {
      setLoadingDetails(false);
    }
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    const reqHeaders = new Headers();
    reqHeaders.append("x-access-token", token);
    formData.append("file", file, file.name);
    formData.append("type", "image");
    setUploading(true);
    try {
      const res = await fetch(`${Constants.BASE_API_URL}user/upload`, {
        method: "POST",
        headers: reqHeaders,
        body: formData,
      });
      return await res.json();
    } catch {
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      toast.error("Only JPG and PNG files are allowed.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB.");
      return;
    }
    const result = await uploadImage(file);
    if (result?.status && result?.data?.id) {
      setImageId(result.data.id);
      setImageUrl(result.data.url || URL.createObjectURL(file));
      toast.success("Image uploaded");
    } else {
      toast.error("Failed to upload image.");
    }
  };

  const validationSchema = Yup.object({
    websiteLink: Yup.string().url("Enter a valid URL").nullable(),
  });

  const formik = useFormik({
    initialValues: {
      websiteLink: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!imageId) {
        toast.error("Please upload an image first.");
        return;
      }
      if (!shareToAll && clubIdsRef.current.length === 0) {
        toast.error("Please select at least one club, or enable Share to All Clubs.");
        return;
      }
      setSubmitting(true);
      try {
        const body = {
          id,
          id_item_logo: imageId.toString(),
          target_org_ids: shareToAll ? [] : clubIdsRef.current.map(Number),
          ...(values.websiteLink ? { url_link: values.websiteLink } : { url_link: "" }),
        };
        const res = await ApiServices.PostApiCall(
          ApiEndpoint.MASTER_ORG_ADVERTISEMENT_EDIT,
          JSON.stringify(body),
          headers()
        );
        if (res?.status) {
          toast.success(res.message || "Advertisement updated", {
            onClose: () => navigate("/master-org/advertisements"),
            autoClose: 2000,
          });
        } else {
          toast.error(res?.message || "Failed to update advertisement");
        }
      } catch {
        toast.error("Something went wrong. Please try again.");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#f7fafc",
      borderRadius: "8px",
    },
  };

  const handleClubChange = (e) => {
    const value = e.target.value;
    clubIdsRef.current = value;
    setSelectedClubIds(value);
  };

  const handleSelectAll = () => {
    const allIds = subOrgs.map((o) => o.id);
    clubIdsRef.current = allIds;
    setSelectedClubIds(allIds);
  };

  const handleClearAll = () => {
    clubIdsRef.current = [];
    setSelectedClubIds([]);
  };

  if (loadingDetails) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress color={isDark ? "secondary" : "primary"} />
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Edit Advertisement"
        subtitle="Update this mobile app advertisement for members of your connected clubs."
        breadcrumbs={[
          { label: "Club Partner" },
          { label: "Advertisements", href: "/master-org/advertisements" },
          { label: "Edit" },
        ]}
      >
        <ActionButton
          variant="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/master-org/advertisements")}
        >
          Back
        </ActionButton>
      </PageHeader>

      <MobileOnlyAdBanner />

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Main form */}
          <Grid item xs={12} md={8}>
            <Card title="Advertisement Details">
              <Grid container spacing={2}>
                {/* Destination URL */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Destination URL (optional)"
                    name="websiteLink"
                    value={formik.values.websiteLink}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.websiteLink && Boolean(formik.errors.websiteLink)}
                    helperText={(formik.touched.websiteLink && formik.errors.websiteLink) || "Where members go when they tap the ad in the mobile app"}
                    placeholder="https://example.com"
                    sx={inputSx}
                  />
                </Grid>

                {/* Share to All toggle */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={shareToAll}
                        onChange={(e) => {
                          setShareToAll(e.target.checked);
                          if (e.target.checked) {
                            clubIdsRef.current = [];
                            setSelectedClubIds([]);
                          }
                        }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": { color: isDark ? "#818cf8" : "#1976d2" },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: isDark ? "#6366f1" : "#1976d2",
                          },
                        }}
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#e2e8f0" : "#334155" }}>
                        Share to all clubs
                      </Typography>
                    }
                  />
                  {shareToAll && (
                    <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", mt: 0.5, ml: 0.5 }}>
                      This ad will be shown to members of all your connected clubs.
                    </Typography>
                  )}
                </Grid>

                {/* Target Clubs multi-select */}
                {!shareToAll && (
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={inputSx}>
                      <InputLabel>Target Clubs *</InputLabel>
                      <Select
                        multiple
                        value={selectedClubIds}
                        onChange={handleClubChange}
                        input={<OutlinedInput label="Target Clubs *" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((id) => {
                              const org = subOrgs.find((o) => o.id === id);
                              return (
                                <Chip
                                  key={id}
                                  label={org?.name || `Club #${id}`}
                                  size="small"
                                  sx={{
                                    backgroundColor: isDark ? "rgba(99,102,241,0.2)" : "rgba(99,102,241,0.08)",
                                    color: isDark ? "#a5b4fc" : "#6366f1",
                                    borderRadius: "6px",
                                  }}
                                />
                              );
                            })}
                          </Box>
                        )}
                      >
                        <ListSubheader sx={{ backgroundColor: isDark ? "#1e1e2e" : "#fff", display: "flex", gap: 1, py: 1 }}>
                          <Button size="small" onClick={handleSelectAll} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                            Select all
                          </Button>
                          <Button size="small" onClick={handleClearAll} sx={{ textTransform: "none", fontSize: "0.75rem" }}>
                            Clear
                          </Button>
                        </ListSubheader>
                        {loadingSubOrgs ? (
                          <MenuItem disabled>
                            <CircularProgress size={16} sx={{ mr: 1 }} /> Loading clubs...
                          </MenuItem>
                        ) : subOrgs.length === 0 ? (
                          <MenuItem disabled>No clubs connected yet</MenuItem>
                        ) : (
                          subOrgs.map((org) => (
                            <MenuItem key={org.id} value={org.id}>
                              <Checkbox checked={selectedClubIds.includes(org.id)} size="small" />
                              <ListItemText primary={org.name || `Club #${org.id}`} />
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      <FormHelperText>Members of selected clubs will see this ad in their mobile app</FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
            </Card>
          </Grid>

          {/* Image Upload Sidebar */}
          <Grid item xs={12} md={4}>
            <Card title="Ad Image">
              <Box
                sx={{
                  border: isDark ? "2px dashed rgba(255,255,255,0.1)" : "2px dashed #e2e8f0",
                  borderRadius: "12px",
                  p: 3,
                  textAlign: "center",
                  backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#f8fafc",
                  transition: "all 0.2s",
                  "&:hover": {
                    borderColor: isDark ? "#6366f1" : "#1976d2",
                    backgroundColor: isDark ? "rgba(99,102,241,0.05)" : "rgba(25,118,210,0.04)",
                  },
                }}
              >
                {imageUrl ? (
                  <Box>
                    <Box
                      sx={{ position: "relative", display: "inline-block", cursor: "pointer" }}
                      onClick={() => setPreviewOpen(true)}
                    >
                      <img
                        src={imageUrl}
                        alt="Ad preview"
                        style={{ width: "100%", aspectRatio: "4 / 5", objectFit: "cover", borderRadius: 8 }}
                      />
                    </Box>
                    <Typography sx={{ mt: 1, fontSize: "0.75rem", color: isDark ? "#718096" : "#94a3b8" }}>
                      Click image to preview
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <CloudUploadIcon sx={{ fontSize: 48, color: isDark ? "#475569" : "#cbd5e1", mb: 1 }} />
                    <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#94a3b8" : "#64748b", mb: 0.5 }}>
                      Upload your ad image
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#64748b" : "#94a3b8" }}>
                      JPG or PNG · max 5 MB · 600 × 800 px
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />}
                  sx={{
                    mt: 2,
                    textTransform: "none",
                    borderRadius: "8px",
                    borderColor: isDark ? "rgba(255,255,255,0.2)" : "#e2e8f0",
                    color: isDark ? "#ffffff" : "#1a202c",
                  }}
                >
                  {uploading ? "Uploading..." : imageUrl ? "Change Image" : "Choose Image"}
                  <input type="file" hidden accept="image/jpeg,image/png" onChange={handleFileChange} />
                </Button>
              </Box>
            </Card>

            <Box sx={{ mt: 3 }}>
              <Card title="Image Specification">
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", lineHeight: 2 }}>
                  Recommended: <strong>600 × 800 px (4:5 portrait)</strong>
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#94a3b8" : "#64748b", lineHeight: 1.8 }}>
                  Shown in the mobile app feed for members of your targeted clubs.
                </Typography>
              </Card>
            </Box>

            <Box sx={{ mt: 3 }}>
              <ActionButton
                variant="primary"
                fullWidth
                size="large"
                type="submit"
                loading={submitting}
                disabled={submitting || uploading}
              >
                {submitting ? "Saving..." : "Update Advertisement"}
              </ActionButton>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: isDark ? "#1a1a1a" : "#fff", borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
          <Typography sx={{ fontWeight: 700, color: isDark ? "#fff" : "#1a202c" }}>Image Preview</Typography>
          <IconButton onClick={() => setPreviewOpen(false)} sx={{ color: isDark ? "#cbd5e1" : "#718096" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, textAlign: "center" }}>
          {imageUrl && <img src={imageUrl} alt="Ad" style={{ maxWidth: "100%", borderRadius: 8 }} />}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid #e2e8f0" }}>
          <Button onClick={() => setPreviewOpen(false)} sx={{ textTransform: "none" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = (state) => ({ profile: state.user.profile });
export default connect(mapStateToProps)(MasterOrgEditAdvertisement);
